import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiRequestError } from "../../api/client";
import {
  CASE_STATUSES,
  CASE_STATUS_LABELS,
  PROFESSIONS,
  professionLabel,
  SITE_ATMOSPHERES,
  type CaseStatus,
} from "../../../shared/constants";
import { CaseStatusPill, formatDate, personName } from "./ui";

interface AdminCase {
  id: string;
  user_email: string;
  user_last_name: string | null;
  user_first_name: string | null;
  status: string;
  profession: string;
  office_name: string | null;
  representative_name: string | null;
  updated_at: string;
}

// No.12 運営: 案件一覧。集計カード・フィルタ・キーワード検索。
export function AdminCasesPage() {
  const [cases, setCases] = useState<AdminCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [professionFilter, setProfessionFilter] = useState<string>("all");

  useEffect(() => {
    api
      .get<{ cases: AdminCase[] }>("/api/admin/cases")
      .then((res) => setCases(res.cases))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const stats = useMemo(() => {
    return {
      total: cases.length,
      inProduction: cases.filter((c) => c.status === "in_production").length,
      awaitingPayment: cases.filter((c) => c.status === "awaiting_payment").length,
      published: cases.filter((c) => c.status === "published").length,
    };
  }, [cases]);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return cases.filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (professionFilter !== "all" && c.profession !== professionFilter)
        return false;
      if (kw) {
        const hay = [
          c.office_name,
          c.representative_name,
          c.user_email,
          personName(c.user_last_name, c.user_first_name, ""),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      return true;
    });
  }, [cases, keyword, statusFilter, professionFilter]);

  return (
    <section>
      <div className="admin-page-head">
        <div>
          <div className="admin-eyebrow">運営管理</div>
          <h1>案件一覧</h1>
          <p className="muted">
            クライアントのホームページ制作案件を一覧で管理します。
          </p>
        </div>
      </div>

      <div className="stat-grid">
        <div className="card stat">
          <div className="stat-label">全案件数</div>
          <div className="stat-value">{loading ? "…" : stats.total}</div>
          <div className="stat-label">すべての案件</div>
        </div>
        <div className="card stat">
          <div className="stat-label">制作中</div>
          <div className="stat-value">{loading ? "…" : stats.inProduction}</div>
          <div className="stat-label">現在進行中</div>
        </div>
        <div className="card stat">
          <div className="stat-label">入金待ち</div>
          <div className="stat-value">
            {loading ? "…" : stats.awaitingPayment}
          </div>
          <div className="stat-label">入金確認待ち</div>
        </div>
        <div className="card stat">
          <div className="stat-label">公開済み</div>
          <div className="stat-value">{loading ? "…" : stats.published}</div>
          <div className="stat-label">公開完了</div>
        </div>
      </div>

      <div className="card admin-toolbar">
        <label className="filter-field grow">
          <span>キーワード検索</span>
          <input
            type="text"
            placeholder="事務所名・代表者名・メールで検索"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </label>
        <label className="filter-field">
          <span>ステータス</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">すべて</option>
            {CASE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {CASE_STATUS_LABELS[s as CaseStatus]}
              </option>
            ))}
          </select>
        </label>
        <label className="filter-field">
          <span>士業種別</span>
          <select
            value={professionFilter}
            onChange={(e) => setProfessionFilter(e.target.value)}
          >
            <option value="all">すべて</option>
            {PROFESSIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="card">
        {error ? (
          <div className="alert alert-error">{error}</div>
        ) : loading ? (
          <p className="muted">読み込み中...</p>
        ) : filtered.length === 0 ? (
          <p className="muted">該当する案件がありません。</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>事務所名 / 代表者</th>
                <th>士業種別</th>
                <th>ステータス</th>
                <th>担当</th>
                <th>更新日</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div className="cell-strong">
                      {c.office_name ?? "（未設定）"}
                    </div>
                    <div className="muted small">
                      {c.representative_name ??
                        personName(c.user_last_name, c.user_first_name, c.user_email)}
                    </div>
                  </td>
                  <td>{professionLabel(c.profession)}</td>
                  <td>
                    <CaseStatusPill status={c.status} />
                  </td>
                  <td>運営</td>
                  <td>{formatDate(c.updated_at)}</td>
                  <td>
                    <Link className="btn btn-ghost btn-sm" to={`/admin/cases/${c.id}`}>
                      詳細
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <GuestSalesSection />
    </section>
  );
}

interface GuestCase {
  id: string;
  guest_token: string;
  status: string;
  profession: string;
  office_name: string | null;
  proposals_count: number;
  created_at: string;
  share_path: string;
}

// ゲスト営業案件: 見込み客のHP案を先に作って共有リンクを渡す。
function GuestSalesSection() {
  const [cases, setCases] = useState<GuestCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    office_name: "",
    profession: "",
    current_url: "",
    atmosphere: "",
    strengths: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setField = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const reload = () => {
    api
      .get<{ cases: GuestCase[] }>("/api/admin/guest-cases")
      .then((res) => setCases(res.cases))
      .catch(() => setCases([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, []);

  const create = async () => {
    setError(null);
    setBusy(true);
    try {
      await api.post<{ share_path: string }>("/api/admin/guest-cases", {
        office_name: form.office_name.trim(),
        profession: form.profession,
        current_url: form.current_url.trim() || undefined,
        atmosphere: form.atmosphere || undefined,
        strengths: form.strengths.trim() || undefined,
      });
      setForm({ office_name: "", profession: "", current_url: "", atmosphere: "", strengths: "" });
      reload();
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : "ゲスト案件の作成に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  const shareUrl = (path: string) =>
    typeof window !== "undefined" ? `${window.location.origin}${path}` : path;

  const canCreate = form.office_name.trim() !== "" && form.profession !== "" && !busy;

  return (
    <div className="card guest-admin">
      <div className="admin-page-head">
        <div>
          <h2>ゲスト営業案件</h2>
          <p className="muted small">
            見込み客のホームページ案を先に作成し、共有リンク（/g/…）を渡して提案します。
            クレジット消費はありません（運営コスト）。
          </p>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="guest-create-form">
        <div className="form-row">
          <label>
            事務所名 <span className="req">必須</span>
            <input
              type="text"
              placeholder="例: 見込み法律事務所"
              value={form.office_name}
              onChange={(e) => setField("office_name", e.target.value)}
            />
          </label>
          <label>
            士業種別 <span className="req">必須</span>
            <select
              value={form.profession}
              onChange={(e) => setField("profession", e.target.value)}
            >
              <option value="">選択してください</option>
              {PROFESSIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="form-row">
          <label>
            現在のサイトURL（任意・解析に利用）
            <input
              type="url"
              placeholder="https://..."
              value={form.current_url}
              onChange={(e) => setField("current_url", e.target.value)}
            />
          </label>
          <label>
            サイトの雰囲気（任意）
            <select
              value={form.atmosphere}
              onChange={(e) => setField("atmosphere", e.target.value)}
            >
              <option value="">指定なし</option>
              {SITE_ATMOSPHERES.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label>
          強み・特徴（任意）
          <textarea
            rows={2}
            placeholder="例: 相続に強い地域密着の事務所です。"
            value={form.strengths}
            onChange={(e) => setField("strengths", e.target.value)}
          />
        </label>
        <button className="btn btn-primary" onClick={create} disabled={!canCreate}>
          {busy ? "作成中..." : "ゲスト案件を作成（6案生成）"}
        </button>
      </div>

      {loading ? (
        <p className="muted">読み込み中...</p>
      ) : cases.length === 0 ? (
        <p className="muted">ゲスト営業案件はまだありません。</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>事務所名</th>
              <th>士業種別</th>
              <th>ステータス</th>
              <th>生成案数</th>
              <th>共有リンク</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {cases.map((c) => (
              <tr key={c.id}>
                <td className="cell-strong">{c.office_name ?? "（未設定）"}</td>
                <td>{professionLabel(c.profession)}</td>
                <td>
                  <CaseStatusPill status={c.status} />
                </td>
                <td>{c.proposals_count} / 6</td>
                <td>
                  <a href={c.share_path} target="_blank" rel="noreferrer">
                    {shareUrl(c.share_path)}
                  </a>
                </td>
                <td>
                  <Link className="btn btn-ghost btn-sm" to={`/admin/cases/${c.id}`}>
                    詳細
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
